import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';

export interface MentionMember {
  readonly id: string;
  readonly name: string;
  readonly image: string | null;
}

@Component({
  selector: 'lib-mention-autocomplete',
  templateUrl: './mention-autocomplete.component.html',
  styleUrls: ['./mention-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class MentionAutocompleteComponent {
  readonly members: InputSignal<MentionMember[]> = input.required<MentionMember[]>();
  readonly filterText: InputSignal<string> = input<string>('');

  readonly memberSelected: OutputEmitterRef<MentionMember> = output<MentionMember>();

  readonly activeIndex: WritableSignal<number> = signal<number>(0);

  readonly filteredMembers: Signal<MentionMember[]> = computed(() => {
    const query: string = this.filterText().toLowerCase();
    const list: MentionMember[] = this.members();
    if (!query) return list;
    return list.filter((m: MentionMember) => m.name.toLowerCase().includes(query));
  });

  moveUp(): void {
    const max: number = this.filteredMembers().length;
    if (max === 0) return;
    this.activeIndex.update((i: number) => (i - 1 + max) % max);
  }

  moveDown(): void {
    const max: number = this.filteredMembers().length;
    if (max === 0) return;
    this.activeIndex.update((i: number) => (i + 1) % max);
  }

  confirmSelection(): void {
    const members: MentionMember[] = this.filteredMembers();
    const idx: number = this.activeIndex();
    if (members[idx]) {
      this.selectMember(members[idx]);
    }
  }

  selectMember(member: MentionMember): void {
    this.memberSelected.emit(member);
    this.activeIndex.set(0);
  }

  resetIndex(): void {
    this.activeIndex.set(0);
  }
}
